/**
 * INSTANCE ventes (deferred payment) + CHIFA card custodies. The list[] is
 * what the Instance dashboard reads; custodies[] is the held-card registry.
 */
import { dateOffsetYmd } from "../uuid.js";

export function buildInstances(clients, produits, stockLots, users) {
  const caissier = users.find((u) => u.id === "beta-user-caissier-1") || users[2];
  const caissierProj = { id: caissier.id, nom: caissier.nom, prenom: caissier.prenom };
  const findProd = (id) => produits.find((p) => p.id === id);
  const findLot = (pid) =>
    stockLots.find((l) => l.produit_id === pid && l.quantite_actuelle > 0)?.id ||
    null;

  let counter = 1;
  let custodyCounter = 1;
  const list = [];
  const custodies = [];

  const mkInstance = (clientIdx, daysAgo, items, opts = {}) => {
    const id = `instance-${String(counter).padStart(3, "0")}`;
    const num = `INS-2026-${String(counter).padStart(3, "0")}`;
    counter += 1;
    const c = clients[clientIdx];
    const details = items.map((it, i) => {
      const p = findProd(it.produit_id);
      const qte = it.quantite;
      const ttc = +(qte * p.ppa).toFixed(2);
      return {
        id: `${id}-det-${i + 1}`,
        produit_id: p.id,
        produit: { id: p.id, nom_commercial: p.nom_commercial, ppa: p.ppa, dci: p.dci, forme_pharma: p.forme_pharma, dosage: p.dosage },
        stock_lot_id: findLot(p.id),
        quantite: qte,
        prix_unitaire: p.ppa,
        remise_pct: 0,
        tva_pct: p.tva_pct ?? 0,
        montant_ligne_ttc: ttc,
        montant_pec_chifa: 0,
        montant_patient: ttc,
      };
    });
    const totalTtc = +details.reduce((s, d) => s + d.montant_ligne_ttc, 0).toFixed(2);

    let custodyId = null;
    if (opts.carte_chifa_retenue && c.carte_chifa) {
      custodyId = `custody-${String(custodyCounter).padStart(3, "0")}`;
      custodyCounter += 1;
      custodies.push({
        id: custodyId,
        client_id: c.id,
        client: { id: c.id, nom: c.nom, prenom: c.prenom, telephone: c.telephone },
        carte_numero: c.carte_chifa,
        vente_id: id,
        date_retention: dateOffsetYmd(-daysAgo),
        date_restitution: opts.statut_paiement === "PAYE" ? dateOffsetYmd(-(daysAgo - 1)) : null,
        statut: opts.statut_paiement === "PAYE" ? "RETURNED" : "HELD",
        notes: "Carte retenue en garantie de paiement",
      });
    }

    list.push({
      id,
      numero_interne: num,
      type_vente: "INSTANCE",
      statut: "VALIDEE",
      date_vente: dateOffsetYmd(-daysAgo) + "T10:00:00.000Z",
      client_id: c.id,
      client: { id: c.id, nom: c.nom, prenom: c.prenom, telephone: c.telephone, carte_chifa: c.carte_chifa },
      caissier_id: caissier.id,
      caissier: caissierProj,
      caisse_id: "caisse-001",
      remise_montant: 0,
      remise_pct: 0,
      montant_total_ht: +(totalTtc / 1.19).toFixed(2),
      montant_total_tva: +(totalTtc - totalTtc / 1.19).toFixed(2),
      montant_total_ttc: totalTtc,
      montant_pec_chifa: 0,
      montant_a_payer: totalTtc,
      statut_paiement: opts.statut_paiement || "NON_PAYE",
      date_paiement: opts.statut_paiement === "PAYE" ? dateOffsetYmd(-(daysAgo - 1)) : null,
      carte_chifa_retenue: !!opts.carte_chifa_retenue,
      carte_numero: opts.carte_chifa_retenue ? c.carte_chifa : null,
      custody_id: custodyId,
      mode_paiement: "INSTANCE",
      notes: opts.notes || "",
      details,
      created_at: dateOffsetYmd(-daysAgo) + "T10:00:00.000Z",
      updated_at: dateOffsetYmd(-(daysAgo - 1)) + "T10:00:00.000Z",
    });
  };

  // 3 NON_PAYE (1-15 days old)
  mkInstance(2, 1, [{ produit_id: "p-007", quantite: 1 }, { produit_id: "p-035", quantite: 1 }], { notes: "Reviendra demain" });
  mkInstance(5, 7, [{ produit_id: "p-202", quantite: 1 }, { produit_id: "p-203", quantite: 2 }], { notes: "" });
  mkInstance(11, 15, [{ produit_id: "p-015", quantite: 1 }], { notes: "Madame Belhadj — habituée" });

  // 2 NON_PAYE + carte_chifa_retenue=true → linked HELD custody
  mkInstance(7, 5, [{ produit_id: "p-018", quantite: 1 }, { produit_id: "p-043", quantite: 1 }], { carte_chifa_retenue: true, notes: "Carte CHIFA retenue jusqu'au règlement" });
  mkInstance(3, 10, [{ produit_id: "p-008", quantite: 2 }], { carte_chifa_retenue: true, notes: "Patient appelé 2 fois sans réponse" });

  // 1 PAYE recently
  mkInstance(1, 3, [{ produit_id: "p-011", quantite: 1 }, { produit_id: "p-019", quantite: 1 }], { statut_paiement: "PAYE", notes: "Réglé hier" });

  return { list, custodies };
}
